import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, firstValueFrom, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { UserRole } from '../enums';
import {
  IApiResponse,
  IUser,
  IOrgUnitRef,
  IRoleAssignment,
  ILoginRequest,
  ILoginResponse,
  IAuthTokenResponse,
  IUserInfo,
  IMeApiResponse,
  IBackendRoleAssignment,
} from '../models';
import { Permission, Permissions } from '../permissions';
import { API } from '../constants';
import { environment } from '../../../environments/environment';
import { SignalRService } from './signalr.service';

const MOCK_CURRENT_USER: IUser = {
  employeeId:      'EMP-001',
  fullName:        'أحمد محمد العتيبي',
  title:           'مدير إدارة الجودة',
  department:      'إدارة الجودة',
  parentDepartment:'الإدارة العامة للجودة',
  email:           'ahmed@example.com',
  mobile:          '0501234567',
  roles:           [],
  isSystemAdmin:   false,
  roleAssignments: [
    {
      id: 'ra-001',
      roleId: 'role-001',
      roleName: 'معد عمليات',
      permissions: [
        'process.create',
        'process.edit-axes',
        'process.send_review',
        'process.view',
      ] as Permission[],
      orgUnits: [
        { id: 1, name: 'إدارة الإعلام', parentId: 101, parentName: 'الإدارة العامة للتواصل' },
        { id: 2, name: 'إدارة المالية', parentId: 102, parentName: 'الإدارة العامة للشؤون المالية' },
      ],
    },
    {
      id: 'ra-002',
      roleId: 'role-002',
      roleName: 'مراجع أهداف الجودة',
      permissions: ['goals.view', 'process.review'] as Permission[],
      orgUnits: [
        { id: 3, name: 'إدارة الموارد البشرية', parentId: 103, parentName: 'الإدارة العامة للموارد' },
      ],
    },
  ],
  // Demo build: all permissions granted so every action button is visible during the presentation
  permissions: Object.values(Permissions) as Permission[],
  digitalSignature: undefined,
  avatarUrl: undefined,
};

@Injectable({ providedIn: 'root' })
export class AuthService {
    private readonly http = inject(HttpClient);
    private readonly router = inject(Router);
    private readonly signalRService = inject(SignalRService);

    private readonly _accessToken = signal<string | null>(null);

    private readonly _isInitialized = signal(false);

    readonly currentUser       = signal<IUser | null>(null);
    readonly isAuthenticated   = signal(false);
    readonly isUsingMockData   = signal(false);
    readonly isInitialized     = this._isInitialized.asReadonly();

    /**
     * Restores the session on app boot — refreshes the token and, on success,
     * waits for the full profile (roleAssignments included) before resolving.
     * Wired via `provideAppInitializer` in app.config.ts so no route guard
     * runs against a partially-hydrated user.
     * @returns Promise<void> - resolves once auth state (or its absence) is known
     */
    async initializeAuth(): Promise<void> {
        await this.refreshToken();
        this._isInitialized.set(true);
    }

    getAccessToken(): string | null {
        return this._accessToken();
    }

    async login(credentials: ILoginRequest): Promise<ILoginResponse> {
        const response = await firstValueFrom(
            this.http.post<IApiResponse<ILoginResponse>>(
                `${environment.apiUrl}${API.AUTH.LOGIN}`,
                credentials
            )
        );
        if (!response.success || !response.data) {
            throw Object.assign(new Error(response.message), { error: response });
        }
        return response.data;
    }

    /**
     * Authenticates a locally-created (non-AD) user directly, bypassing the OTP flow used for AD users.
     * @param credentials - Local username and password
     * @throws Error - If the credentials are invalid, the account is AD-linked, or has no password set
     */
    async localLogin(credentials: ILoginRequest): Promise<void> {
        const response = await firstValueFrom(
            this.http.post<IApiResponse<IAuthTokenResponse>>(
                `${environment.apiUrl}${API.AUTH.LOCAL_LOGIN}`,
                credentials,
                { withCredentials: true }
            )
        );
        if (!response.success || !response.data) {
            throw Object.assign(new Error(response.message), { error: response });
        }
        await this._applyAuthState(response.data);
    }

    /**
     * Authenticates a system admin directly, bypassing the OTP flow used for regular users.
     * @param credentials - Admin username and password
     * @throws Error - If the credentials are invalid or the account is locked
     */
    async adminLogin(credentials: ILoginRequest): Promise<void> {
        const response = await firstValueFrom(
            this.http.post<IApiResponse<IAuthTokenResponse>>(
                `${environment.apiUrl}${API.AUTH.ADMIN_LOGIN}`,
                credentials,
                { withCredentials: true }
            )
        );
        if (!response.success || !response.data) {
            throw Object.assign(new Error(response.message), { error: response });
        }
        await this._applyAuthState(response.data);
    }

    async verifyOtp(transactionId: string, otpCode: string): Promise<void> {
        const response = await firstValueFrom(
            this.http.post<IApiResponse<IAuthTokenResponse>>(
                `${environment.apiUrl}${API.AUTH.VERIFY_OTP}`,
                { transactionId, otpCode },
                { withCredentials: true }
            )
        );
        if (!response.success || !response.data) {
            throw Object.assign(new Error(response.message), { error: response });
        }
        await this._applyAuthState(response.data);
    }

    async refreshToken(): Promise<boolean> {
        try {
            const response = await firstValueFrom(
                this.http.post<IApiResponse<IAuthTokenResponse>>(
                    `${environment.apiUrl}${API.AUTH.REFRESH}`,
                    {},
                    { withCredentials: true }
                )
            );
            if (response.success && response.data) {
                await this._applyAuthState(response.data);
                return true;
            }
            this._clearSession();
            return false;
        } catch {
            this._clearSession();
            return false;
        }
    }

    async logout(): Promise<void> {
        await firstValueFrom(
            this.http.post(
                `${environment.apiUrl}${API.AUTH.LOGOUT}`,
                {},
                { withCredentials: true }
            )
        ).catch(() => {});
        await this.signalRService.disconnect();
        this._clearSession();
        this.router.navigate(['/login']);
    }

    /**
     * Fetches the full user profile from backend.
     * Uses the development mock only when explicitly enabled; real failures
     * clear session state and remain errors.
     * @returns Observable<IUser>
     */
    getMe(): Observable<IUser> {
        if (environment.useMockData) {
            this.isUsingMockData.set(true);
            this.currentUser.set(MOCK_CURRENT_USER);
            return of(MOCK_CURRENT_USER);
        }
        return this.http.get<IApiResponse<IMeApiResponse>>(
            `${environment.apiUrl}${API.AUTH.ME}`
        ).pipe(
            map(response => {
                if (response.success && response.data) {
                    const enriched = this._mapMeResponse(response.data);
                    this.isUsingMockData.set(false);
                    this.currentUser.set(enriched);
                    return enriched;
                }
                throw new Error(response.message);
            }),
            catchError((error: unknown) => {
                this._clearSession();
                return throwError(() => error);
            })
        );
    }

    isLoggedIn(): boolean {
        return this.isAuthenticated();
    }

    hasRole(role: string): boolean {
        return this.currentUser()?.roles?.includes(role as UserRole) ?? false;
    }

    hasAnyRole(roles: string[]): boolean {
        return roles.some(r => this.hasRole(r));
    }

    hasPermission(permission: Permission): boolean {
        if (this.currentUser()?.isSystemAdmin) return true;
        return this.currentUser()?.permissions?.includes(permission) ?? false;
    }

    /**
     * Checks whether the current user holds a permission, either globally or scoped to a specific org unit.
     * @param permission - Permission key to check
     * @param orgUnitId - Org unit to check the permission against
     * @returns boolean
     */
    hasPermissionInOrgUnit(permission: Permission, orgUnitId: number): boolean {
        if (this.currentUser()?.isSystemAdmin) return true;
        const assignments = this.currentUser()?.roleAssignments ?? [];
        return assignments.some(a =>
            a.permissions.includes(permission) &&
            (a.orgUnits.length === 0 || a.orgUnits.some(u => u.id === orgUnitId))
        );
    }

    /**
     * Returns every org unit where the current user holds a given permission.
     * An empty array means the permission is held globally (system admin).
     * @param permission - Permission key to check
     * @returns IOrgUnitRef[]
     */
    getOrgUnitsForPermission(permission: Permission): IOrgUnitRef[] {
        if (this.currentUser()?.isSystemAdmin) return [];
        const assignments = this.currentUser()?.roleAssignments ?? [];
        const units: IOrgUnitRef[] = [];
        assignments
            .filter(a => a.permissions.includes(permission))
            .forEach(a => units.push(...a.orgUnits));
        return units.filter((u, i, arr) => arr.findIndex(x => x.id === u.id) === i);
    }

    private async _applyAuthState(tokenResponse: IAuthTokenResponse): Promise<void> {
        this._accessToken.set(tokenResponse.accessToken);
        this.isAuthenticated.set(true);
        this.currentUser.set(this._mapUserInfo(tokenResponse.user));
        await firstValueFrom(this.getMe());
        await this.signalRService.loadInitial();
        await this.signalRService.connect();
    }

    private _mapUserInfo(userInfo: IUserInfo): IUser {
        const roles: UserRole[] = (userInfo.roles ?? []) as UserRole[];
        const isSystemAdmin = roles.includes(UserRole.SystemAdmin);

        return {
            fullName:        userInfo.name,
            employeeId:      userInfo.username,
            permissions:     userInfo.permissions as Permission[],
            title:           '',
            department:      '',
            parentDepartment:'',
            email:           '',
            mobile:          '',
            roles,
            isSystemAdmin,
            roleAssignments: [],
        };
    }

    private _mapMeResponse(me: IMeApiResponse): IUser {
        const roles: UserRole[] = (me.roles ?? []) as UserRole[];
        const isSystemAdmin = me.isSystemAdmin ?? roles.includes(UserRole.SystemAdmin);
        const roleAssignments = (me.roleAssignments ?? []).map(a => this._mapBackendRoleAssignment(a));
        const permissions = roleAssignments.length
            ? this._dedupePermissions(roleAssignments.flatMap(a => a.permissions))
            : ((me.permissions ?? []) as Permission[]);

        return {
            fullName:        me.name,
            // GET /api/auth/me's `username` is the real UserName (login), not the
            // employeeId — unlike AuthTokenResponse.user.username. employeeId isn't
            // in this endpoint's response at all, so keep whatever _mapUserInfo
            // already set from login rather than overwrite it with the UserName.
            employeeId:      this.currentUser()?.employeeId ?? '',
            title:           me.jobTitle ?? '',
            department:      me.department ?? '',
            parentDepartment: me.parentDepartment ?? '',
            email:           me.email ?? '',
            mobile:          me.mobile ?? '',
            roles,
            isSystemAdmin,
            roleAssignments,
            permissions,
            digitalSignature: me.digitalSignature,
        };
    }

    private _mapBackendRoleAssignment(a: IBackendRoleAssignment): IRoleAssignment {
        const orgUnits: IOrgUnitRef[] = a.orgUnitIds.map((id, i) => ({
            id,
            name: a.orgUnitNames?.[i] ?? String(id),
        }));

        return {
            id:          a.id,
            roleId:      a.roleId,
            roleName:    a.roleName,
            permissions: a.permissions as Permission[],
            orgUnits,
        };
    }

    private _dedupePermissions(permissions: Permission[]): Permission[] {
        return permissions.filter((p, i, arr) => arr.indexOf(p) === i);
    }

    private _clearSession(): void {
        this._accessToken.set(null);
        this.isAuthenticated.set(false);
        this.isUsingMockData.set(false);
        this.currentUser.set(null);
    }
}
